import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Compass, Sparkles, LayoutGrid, Heart } from "lucide-react";
import { useFavourites } from "../lib/favourites";
import { fetchVolunteers, fetchVolunteerMeta, fetchVolunteerAnalytics } from "../lib/api";
import { timeAgo } from "../lib/tone";
import { VOLUNTEER_BANNER } from "../lib/volunteerVisuals";
import { OpportunityCard } from "../components/OpportunityCard";
import { VolunteerFilters } from "../components/volunteer/VolunteerFilters";
import { CategoryGrid } from "../components/volunteer/CategoryGrid";
import { MatchView } from "../components/volunteer/MatchView";

const PAGE = 24;
const EMPTY = { q: "", province: "", region: "", city: "", category: "", skill_level: "", commitment_type: "", tags: "", sort: "relevance", radius_km: "25" };
const TABS = [["discover", "Discover", Compass], ["match", "Match me", Sparkles], ["causes", "Causes", LayoutGrid], ["saved", "Saved", Heart]];

const Stat = ({ v, l, testId }) => (
  <div data-testid={testId}><p className="text-3xl font-bold tracking-tight text-white">{v ?? "—"}</p><p className="mt-1 text-sm text-blue-100">{l}</p></div>
);

const Hero = ({ a, onStart }) => (
  <section className="relative overflow-hidden bg-[#1E3A8A]">
    <img src={VOLUNTEER_BANNER} alt="Volunteers restoring a Canadian shoreline" className="absolute inset-0 h-full w-full object-cover opacity-35" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#1E3A8A] via-[#1E3A8A]/85 to-transparent" />
    <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-green-300">Volunteer</p>
      <h1 className="mt-4 max-w-2xl text-4xl font-bold tracking-tight text-white sm:text-5xl" data-testid="volunteer-hero-title">Be the Change Through Volunteering</h1>
      <p className="mt-4 max-w-xl text-base text-blue-100 md:text-lg">Find meaningful opportunities to strengthen your community.</p>
      <button onClick={onStart} className="btn-green mt-8 inline-flex items-center rounded-full px-6 py-3 text-sm font-semibold" data-testid="start-creating-change-cta">Create a Ripple</button>
      <Link to="/organizations" className="ml-3 mt-8 inline-flex items-center rounded-full border border-white/60 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10" data-testid="browse-organizations-cta">Browse Organizations</Link>
      <div className="mt-10 grid max-w-2xl grid-cols-2 gap-6 sm:grid-cols-4">
        <Stat v={a?.total} l="Open opportunities" testId="stat-total" /><Stat v={a?.organizations} l="Organizations" testId="stat-orgs" />
        <Stat v={a?.new_this_week} l="New this week" testId="stat-new" /><Stat v={a?.expiring_this_week} l="Ending this week" testId="stat-expiring" />
      </div>
    </div>
  </section>
);

const Grid = ({ items }) => <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{items.map((o) => <OpportunityCard key={o.id} o={o} />)}</div>;

export default function Volunteer() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([t]) => t === params.get("view")) ? params.get("view") : "discover";
  const [meta, setMeta] = useState({ categories: [], cities: [], regions: [], skills: [], category_info: [] });
  const [a, setA] = useState(null);
  const [f, setF] = useState({ ...EMPTY, category: params.get("category") || "", province: params.get("province") || "" });
  const [geo, setGeo] = useState(null);
  const [items, setItems] = useState(null);
  const [shown, setShown] = useState(PAGE);
  const fav = useFavourites();
  const go = (v) => setParams(v === "discover" ? {} : { view: v });

  useEffect(() => { fetchVolunteerMeta().then(setMeta).catch(() => {}); fetchVolunteerAnalytics().then(setA).catch(() => {}); }, []);
  useEffect(() => { const c = params.get("category"), pr = params.get("province"); if (c || pr) setF((p) => ({ ...p, ...(c ? { category: c } : {}), ...(pr ? { province: pr, region: "", city: "" } : {}) })); }, [params]);
  useEffect(() => {
    const t = setTimeout(() => {
      const q = Object.fromEntries(Object.entries(f).filter(([k, v]) => v && (k !== "radius_km" || geo)));
      fetchVolunteers({ ...q, ...(geo ? { lat: geo.lat, lng: geo.lng } : {}) }).then((r) => { setItems(r); setShown(PAGE); }).catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [f, geo]);

  return (
    <div>
      <Hero a={a} onStart={() => { go("discover"); setTimeout(() => document.getElementById("create-a-ripple")?.scrollIntoView({ behavior: "smooth" }), 50); }} />
      <div className="sticky top-[61px] z-30 border-b border-slate-200 bg-white/95 backdrop-blur-xl">
        <div className="mx-auto grid max-w-7xl grid-cols-4 gap-1 px-2 py-2 sm:flex sm:px-8" role="tablist">
          {TABS.map(([v, l, Icon]) => (
            <button key={v} role="tab" aria-selected={tab === v} onClick={() => go(v)} data-testid={`view-toggle-${v}`}
              className={`flex flex-col items-center gap-1 rounded-2xl px-1 py-1.5 text-[11px] font-semibold sm:flex-row sm:gap-2 sm:rounded-full sm:px-4 sm:py-2 sm:text-sm transition-colors ${tab === v ? "bg-[#1E3A8A] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <Icon size={15} /><span className="whitespace-nowrap">{l}{v === "saved" && fav.count > 0 ? ` (${fav.count})` : ""}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        {tab === "discover" && (
          <>
            <h2 id="create-a-ripple" className="mb-5 scroll-mt-32 text-2xl font-bold tracking-tight text-slate-900" data-testid="create-ripple-heading">Create a Ripple Today</h2>
            <VolunteerFilters meta={meta} f={f} setF={setF} geo={geo} setGeo={setGeo} />
            <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-500" data-testid="volunteer-result-count">
                {items ? `${items.length} ${items.length === 1 ? "opportunity" : "opportunities"}` : "Loading…"}
                {meta.last_import && <span className="ml-2 text-xs text-emerald-700" data-testid="live-import-status">· live listings updated {timeAgo(meta.last_import.value)}</span>}
              </p>
              <button onClick={() => { setF(EMPTY); setGeo(null); }} className="text-sm font-medium text-sky-800 hover:underline" data-testid="volunteer-reset-filters">Reset filters</button>
            </div>
            <div className="mt-4">
              {items === null ? <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <div key={i} className="h-96 animate-pulse rounded-3xl bg-white" />)}</div> : <Grid items={items.slice(0, shown)} />}
            </div>
            {items && items.length > shown && (
              <div className="mt-8 text-center">
                <button onClick={() => setShown((n) => n + PAGE)} data-testid="volunteer-show-more-btn" className="rounded-full border border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-800 hover:border-[#1E3A8A]">Show more ({items.length - shown} remaining)</button>
              </div>
            )}
            {items?.length === 0 && <p className="py-16 text-center text-slate-500" data-testid="volunteer-empty">Nothing here yet — try a wider distance or another cause.</p>}
          </>
        )}
        {tab === "match" && <MatchView meta={meta} geo={geo} />}
        {tab === "causes" && <CategoryGrid info={meta.category_info} onPick={(c) => { setF({ ...EMPTY, category: c }); go("discover"); }} />}
        {tab === "saved" && (
          <div data-testid="saved-view">
            {fav.count === 0
              ? <p className="rounded-3xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-500" data-testid="saved-empty">Tap the heart on any opportunity to save it here. Past events are cleared automatically.</p>
              : <Grid items={fav.items} />}
            <p className="mt-4 text-xs text-slate-500">Saved items stay private in this browser — no account needed.</p>
          </div>
        )}
        <p className="mt-12 text-xs text-slate-500">Only current and upcoming opportunities are shown. Listings link to each organization's own website — please confirm details directly with them.</p>
      </div>
    </div>
  );
}
